import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { AGENTS } from '../../agents/registry.ts';
import { isAgentEnabledFor } from '../../agents/agentAccess.ts';
import { useAuth } from '../../auth/useAuth.ts';
import {
  hasSeenInstallPrompt,
  markInstallPromptSeen,
  markIntroSeen,
} from '../../agents/preferences.ts';
import { canOfferInstall } from '../../pwa/install.ts';
import InstallStep from '../../components/onboarding/InstallStep.tsx';
import './AgentIntro.css';

/**
 * First-run walk-through, shown the first time a user enters an agent (the
 * chooser routes here only when the intro hasn't been seen). Finishing or
 * skipping records the seen-flag so subsequent visits go straight to the
 * console.
 *
 * When the device can install the app to its home screen — and the user hasn't
 * already been offered it — a dedicated install step is appended after the
 * capability steps and before the final "Activate" CTA (see InstallStep).
 *
 * NOTE: the capability-step copy below is still a skeleton tour, to be refined
 * once the full onboarding script is written.
 */

interface InfoStep {
  kind: 'info';
  tag: string;
  title: string;
  body: string;
}

type Step = InfoStep | { kind: 'install' };

const CHAT_STEP: Omit<InfoStep, 'kind'> = {
  tag: 'CHAT',
  title: 'Talk to your agent',
  body: 'Ask questions in plain language. Replies stream in token-by-token, and every turn is grounded in what the agent has already learned.',
};

const MEMORY_STEP: Omit<InfoStep, 'kind'> = {
  tag: 'MEMORY',
  title: 'Watch it think',
  body: 'The memory graph shows what the agent knows and which memories it pulls into context for the current turn — connections light up as it reasons.',
};

const CHANNELS_STEP: Omit<InfoStep, 'kind'> = {
  tag: 'CHANNELS',
  title: 'Reach it anywhere',
  body: 'Email your agent, or verify your phone to text it — it replies on the same channel, acting on your behalf and staying in context. Turn each on under "Email my agents" / "Text my agent" in your account settings (/app/account#channels).',
};

/** Per-agent capability tour. Uno keeps the original script; Vera's adds
 *  her research + workflow-automation + scheduling surface. */
function tourStepsFor(slug: string): readonly Omit<InfoStep, 'kind'>[] {
  if (slug === 'vera') {
    return [
      CHAT_STEP,
      MEMORY_STEP,
      {
        tag: 'RESEARCH',
        title: 'Research anything',
        body: 'Vera searches the web on any topic and digs into stocks — quotes, fundamentals, earnings, news, and insider trades — reading full pages and citing where each fact came from.',
      },
      {
        tag: 'WORKFLOWS',
        title: 'Automate multi-step work',
        body: 'Ask Vera to save a repeatable task as a workflow — a named sequence of steps she runs end-to-end on request, writing documents or sending you the results. Review, edit, and roll back every version from the Jobs panel.',
      },
      {
        tag: 'SCHEDULES',
        title: 'Put it on a schedule',
        body: 'Any workflow can run automatically — every morning, weekly, or once at a set time. Results arrive by email or text with a one-tap link to stop the schedule, and you can toggle schedules in the Jobs panel or just ask Vera.',
      },
      CHANNELS_STEP,
    ];
  }
  return [
    CHAT_STEP,
    MEMORY_STEP,
    {
      tag: 'TOOLS',
      title: 'Tools that do real work',
      body: 'Your agent can pull live weather and stock-market information, and keep lists, notes, and documents in files on your behalf — every action shows up in the tool log.',
    },
    CHANNELS_STEP,
  ];
}

export default function AgentIntro() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  // Decide the step list once at mount: changing it mid-tour would be jarring,
  // and the install prompt (if any) has almost always been captured by now.
  const [steps] = useState<readonly Step[]>(() => {
    const base: Step[] = tourStepsFor(slug ?? '').map((s) => ({ kind: 'info', ...s }));
    if (!hasSeenInstallPrompt() && canOfferInstall()) {
      base.push({ kind: 'install' });
    }
    return base;
  });

  const current = steps[step];

  // Reaching the install step counts as "offered" — so it shows at most once
  // per device and never reappears on a later agent's tour.
  useEffect(() => {
    if (current.kind === 'install') {
      markInstallPromptSeen();
    }
  }, [current]);

  // Only agents THIS user can enter have an intro/console; anything else
  // returns to the chooser rather than touring an agent that can't be
  // entered (group-gated agents like Vera included — FR-ACCESS-1).
  if (!slug || !isAgentEnabledFor(slug, user)) {
    return <Navigate to="/app" replace />;
  }
  const agent = AGENTS[slug];

  function enterConsole() {
    markIntroSeen(agent.slug);
    navigate(`/agent/${agent.slug}`, { replace: true });
  }

  const isLast = step === steps.length - 1;

  return (
    <section className="om-intro">
      <div className="om-intro__inner">
        <header className="om-intro__head">
          <p className="om-intro__eyebrow">
            {agent.agentNumber} · {agent.role}
          </p>
          <h1 className="om-intro__title">Meet {agent.name}</h1>
        </header>

        <div className="om-intro__card">
          {current.kind === 'install' ? (
            <InstallStep agentName={agent.name} />
          ) : (
            <>
              <span className="om-intro__tag">{current.tag}</span>
              <h2 className="om-intro__step-title">{current.title}</h2>
              <p className="om-intro__body">{current.body}</p>
            </>
          )}

          <div className="om-intro__dots" aria-hidden="true">
            {steps.map((s, i) => (
              <span
                key={s.kind === 'install' ? 'install' : s.tag}
                className={i === step ? 'om-intro__dot om-intro__dot--active' : 'om-intro__dot'}
              />
            ))}
          </div>
        </div>

        <div className="om-intro__actions">
          <button type="button" className="om-intro__skip" onClick={enterConsole}>
            Skip tour
          </button>
          <div className="om-intro__nav">
            {step > 0 ? (
              <button
                type="button"
                className="om-intro__back"
                onClick={() => setStep((s) => s - 1)}
              >
                Back
              </button>
            ) : null}
            {isLast ? (
              <button type="button" className="om-intro__next" onClick={enterConsole}>
                Activate {agent.name} →
              </button>
            ) : (
              <button
                type="button"
                className="om-intro__next"
                onClick={() => setStep((s) => s + 1)}
              >
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
